import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { StatsigWrapper } from './contexts/StatsigContext';
import LoadingSpinner from './components/LoadingSpinner';

// Eagerly load LandingPage (most critical route - users land here first)
import LandingPage from './pages/LandingPage';

// Lazy load analytics (non-critical)
const Analytics = React.lazy(() => import('@vercel/analytics/react').then(module => ({ default: module.Analytics })));

// Code splitting: Lazy load all other page components
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const NotesPage = React.lazy(() => import('./pages/NotesPage'));
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage'));
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));
const AcceptShare = React.lazy(() => import('./pages/AcceptShare'));
const WaitlistPage = React.lazy(() => import('./pages/WaitlistPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const DocsPage = React.lazy(() => import('./pages/DocsPage'));

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Export for use in AuthContext (logout cleanup)
export { queryClient };

// Protected Route Component (requires active account)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check account status
  if (user.account_status === 'waitlist') {
    return <Navigate to="/waitlist" replace />;
  }

  if (user.account_status === 'suspended') {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Waitlist Route Component (for waitlisted users only)
const WaitlistRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user is active, redirect to dashboard
  if (user.account_status === 'active') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect to dashboard if authenticated, except for landing page)
const PublicRoute: React.FC<{ children: React.ReactNode; allowAuthenticated?: boolean }> = ({ children, allowAuthenticated }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user && !allowAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Minimal loading fallback (doesn't show for already-loaded landing page)
const MinimalLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-black">
    <LoadingSpinner size="lg" color="white" />
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <StatsigWrapper>
          <Router>
            <div className="App min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
              <Suspense fallback={<MinimalLoader />}>
                <Routes>
                  {/* Landing Page - accessible to all */}
                  <Route
                    path="/"
                    element={
                      <PublicRoute allowAuthenticated={true}>
                        <LandingPage />
                      </PublicRoute>
                    }
                  />

                {/* Chat - authenticated users only */}
                <Route
                  path="/chat/:chatId?"
                  element={
                    <ProtectedRoute>
                      <ChatPage />
                    </ProtectedRoute>
                  }
                />

                {/* Notes - authenticated users only */}
                <Route
                  path="/notes"
                  element={
                    <ProtectedRoute>
                      <NotesPage />
                    </ProtectedRoute>
                  }
                />

                {/* Share Link Acceptance - authenticated users only */}
                <Route
                  path="/share/:shareLink"
                  element={
                    <ProtectedRoute>
                      <AcceptShare />
                    </ProtectedRoute>
                  }
                />

                {/* Public Routes */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <PublicRoute>
                      <RegisterPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/terms"
                  element={
                    <PublicRoute allowAuthenticated={true}>
                      <TermsOfServicePage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/privacy"
                  element={
                    <PublicRoute allowAuthenticated={true}>
                      <PrivacyPolicyPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/docs"
                  element={
                    <PublicRoute allowAuthenticated={true}>
                      <DocsPage />
                    </PublicRoute>
                  }
                />

                {/* Waitlist Route */}
                <Route
                  path="/waitlist"
                  element={
                    <WaitlistRoute>
                      <WaitlistPage />
                    </WaitlistRoute>
                  }
                />

                {/* Protected Routes */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/subscription"
                  element={
                    <ProtectedRoute>
                      <SubscriptionPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/checkout"
                  element={
                    <ProtectedRoute>
                      <CheckoutPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />

                  {/* Fallback Route */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>

              {/* Toast Notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-primary)',
                  },
                  success: {
                    style: {
                      background: 'var(--accent-green)',
                      color: 'white',
                    },
                  },
                  error: {
                    style: {
                      background: '#dc2626',
                      color: 'white',
                    },
                  },
                }}
              />

              {/* Vercel Analytics - Lazy loaded */}
              <Suspense fallback={null}>
                <Analytics />
              </Suspense>
            </div>
          </Router>
        </StatsigWrapper>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;